import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Autocomplete, TextField, IconButton, Button,
    Card, CardContent, Chip, CircularProgress, Alert, Switch, FormControlLabel, Grid,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Link as LinkIcon, PersonSearch as PersonSearchIcon } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { initializeDB } from '../../../config/db';
import { JOB_STORE, EMPLOYEE_STORE } from '../../../config/constants';
import { EQUIPMENT_TYPES, FORM_TYPES } from './constants';
import extEquipmentService from '../../../services/extEquipmentService';
import axiosInstance from '../../../axiosInstance';
import SelectSingleEmployeeModal from '../../shared/SelectSingleEmployeeModal';
import { buildLocationOptions } from '../../safety/utils/locationUtils';
import Swal from 'sweetalert2';

export default function ExtEquipmentLinkForm() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const qrToken = searchParams.get('qrToken');
    const linkType = searchParams.get('type') || 'external'; // 'external' or 'internal'
    const equipmentId = searchParams.get('equipmentId');
    const userName = localStorage.getItem('employeeFullName') || '';

    const [jobs, setJobs] = useState([]);
    const [saving, setSaving] = useState(false);
    const [tagInfo, setTagInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
    const [customAssigned, setCustomAssigned] = useState(false);

    const [form, setForm] = useState({
        equipmentType: '',
        unitSubType: '',
        manufacturer: '',
        model: '',
        serialNumber: '',
        capacity: '',
        owner: '',
        equipmentNumber: '',
        assignedEmployeeName: userName,
        assignedEmployeePhone: '',
        assignedCustomName: '',
        assignedCustomPhone: '',
        location: '',
    });

    const locationOptions = useMemo(() => buildLocationOptions(jobs), [jobs]);

    useEffect(() => {
        (async () => {
            setLoading(true); setError(null);
            const db = await initializeDB();
            setJobs(await db.getAll(JOB_STORE) || []);

            // Load current user's phone from employees
            if (userName) {
                try {
                    const allEmps = await db.getAll(EMPLOYEE_STORE);
                    const me = allEmps?.find(e => (e.FullName || e.fullName) === userName);
                    if (me?.Phone || me?.phone) {
                        setForm(prev => ({ ...prev, assignedEmployeePhone: me.Phone || me.phone }));
                    }
                } catch (e) { /* ignore */ }
            }
            if (qrToken) {
                try {
                    if (linkType === 'internal' && equipmentId) {
                        // Internal equipment — get data from scan endpoint
                        const scanRes = await axiosInstance.get(`/ext-equipment/scan/${qrToken}`);
                        const scanData = scanRes.data;
                        setTagInfo(scanData);
                        if (scanData.autoDetected) {
                            // Find location name from job code
                            let locName = '';
                            if (scanData.currentLocation) {
                                const db2 = await initializeDB();
                                const allJobs = await db2.getAll(JOB_STORE);
                                const matchJob = allJobs?.find(j => j.Job === scanData.currentLocation);
                                locName = matchJob?.location || matchJob?.Location || scanData.currentLocation;
                            }
                            setForm(prev => ({
                                ...prev,
                                equipmentType: scanData.autoDetected.equipmentType || prev.equipmentType,
                                unitSubType: scanData.autoDetected.unitSubType || prev.unitSubType,
                                manufacturer: scanData.autoDetected.manufacturer || prev.manufacturer,
                                model: scanData.autoDetected.model || prev.model,
                                serialNumber: scanData.autoDetected.serialNumber || prev.serialNumber,
                                equipmentNumber: scanData.equipment?.code || '',
                                location: locName,
                            }));
                        }
                    } else {
                        const info = await extEquipmentService.getInfo(qrToken);
                        setTagInfo(info);
                    }
                } catch (e) {
                    if (e.response?.status === 404) {
                        setError('QR code not registered.');
                    } else {
                        setError('Failed to load tag info');
                    }
                }
            } else { setError('No QR token. Scan a QR code first.'); }
            setLoading(false);
        })();
    }, [qrToken]);

    const handleField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    const handleEmployeeSelect = (emp) => {
        setForm(prev => ({
            ...prev,
            assignedEmployeeName: emp.FullName || emp.fullName || '',
            assignedEmployeePhone: emp.Phone || emp.phone || emp.eMail || '',
        }));
        setEmployeeModalOpen(false);
    };

    // Auto-detect form type from equipment type
    const formType = useMemo(() => {
        const eqType = EQUIPMENT_TYPES.find(t => t.value === form.equipmentType);
        return eqType?.formType || 'maintenance_safety';
    }, [form.equipmentType]);

    const formTypeConfig = FORM_TYPES.find(f => f.value === formType);

    const handleLink = async () => {
        if (!form.equipmentType) { Swal.fire({ icon: 'error', text: 'Select equipment type' }); return; }
        if (!form.location) { Swal.fire({ icon: 'error', text: 'Select location' }); return; }
        if (!customAssigned && !form.assignedEmployeeName && !form.assignedCustomName) {
            Swal.fire({ icon: 'error', text: 'Assign an employee or enter custom name' }); return;
        }

        const eqType = EQUIPMENT_TYPES.find(t => t.value === form.equipmentType);
        const confirm = await Swal.fire({
            title: 'Link Equipment Tag?',
            html: `<strong>${tagInfo?.tag?.label || tagInfo?.label || 'Tag'}</strong><br/>${eqType?.label || form.equipmentType}${form.model ? ' — ' + form.model : ''}${form.serialNumber ? ' (S/N: ' + form.serialNumber + ')' : ''}`,
            icon: 'question', showCancelButton: true, confirmButtonText: 'Link & Start Inspection',
        });
        if (!confirm.isConfirmed) return;

        try {
            setSaving(true);
            const payload = {
                qrToken,
                formType,
                linkedBy: userName,
                ...form,
            };
            if (linkType === 'internal' && equipmentId) {
                payload.internalEquipmentId = equipmentId;
            }
            const result = await axiosInstance.post('/ext-equipment/link', payload).then(r => r.data).catch(e => {
                if (e.response?.data?.code === 'ALREADY_LINKED') {
                    navigate(`/operations/ext-equipment/profile?qrToken=${qrToken}`);
                    return null;
                }
                throw new Error(e.response?.data?.error || 'Failed');
            });
            if (!result) return;
            navigate(`/operations/ext-equipment/daily-check?qrToken=${qrToken}`);
        } catch (e) {
            Swal.fire({ icon: 'error', text: e.message || 'Failed to link' });
        } finally { setSaving(false); }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}><CircularProgress /></Box>;
    if (error) return (
        <Box sx={{ height: 'calc(100vh - 60px)', backgroundColor: '#f5f5f5', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton onClick={() => navigate(-1)} edge="start"><ArrowBackIcon /></IconButton>
                    <Typography variant="h6" fontWeight={600}>Link Equipment Tag</Typography>
                </Box>
            </Box>
            <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>
        </Box>
    );

    if (tagInfo?.tag?.linked || tagInfo?.linked) {
        return (
            <Box sx={{ height: 'calc(100vh - 60px)', backgroundColor: '#f5f5f5', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate(-1)} edge="start"><ArrowBackIcon /></IconButton>
                        <Typography variant="h6" fontWeight={600}>Equipment Tag</Typography>
                        <Chip label={tagInfo.tag.label} size="small" sx={{ ml: 'auto', fontWeight: 700, fontFamily: 'monospace' }} />
                    </Box>
                </Box>
                <Box sx={{ p: 2, maxWidth: 600, mx: 'auto', width: '100%' }}>
                    <Alert severity="info" sx={{ mb: 2 }}>Already linked.</Alert>
                    <Button variant="contained" fullWidth size="large" sx={{ py: 1.5, fontWeight: 700 }}
                        onClick={() => navigate(`/operations/ext-equipment/profile?qrToken=${qrToken}`)}>
                        View Equipment Profile
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ height: 'calc(100vh - 60px)', backgroundColor: '#f5f5f5', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton onClick={() => navigate(-1)} edge="start"><ArrowBackIcon /></IconButton>
                    <Typography variant="h6" fontWeight={600}>Link Equipment Tag</Typography>
                    {tagInfo?.tag?.label && <Chip label={tagInfo.tag.label} size="small" sx={{ ml: 'auto', fontWeight: 700, fontFamily: 'monospace' }} />}
                </Box>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                <Box sx={{ maxWidth: 600, mx: 'auto' }}>

                    {/* Equipment Contact (was: Assigned To) — moved to top per request */}
                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                <Typography variant="subtitle2" fontWeight={700}>Equipment Contact</Typography>
                                <FormControlLabel
                                    control={<Switch checked={customAssigned} onChange={() => { setCustomAssigned(!customAssigned); setForm(p => ({ ...p, assignedEmployeeName: '', assignedEmployeePhone: '', assignedCustomName: '', assignedCustomPhone: '' })); }} size="small" />}
                                    label={<Typography sx={{ fontSize: '0.8rem' }}>Custom</Typography>}
                                />
                            </Box>
                            {!customAssigned ? (
                                <Grid container spacing={1}>
                                    <Grid item xs={12}>
                                        <TextField label="Employee *" size="small" fullWidth value={form.assignedEmployeeName}
                                            InputLabelProps={{ shrink: true }} InputProps={{ readOnly: true }}
                                            onClick={() => setEmployeeModalOpen(true)}
                                            sx={{ cursor: 'pointer', '& .MuiInputBase-input': { cursor: 'pointer' } }} />
                                    </Grid>
                                    {form.assignedEmployeePhone && (
                                        <Grid item xs={12}>
                                            <TextField label="Phone" size="small" fullWidth value={form.assignedEmployeePhone} disabled InputLabelProps={{ shrink: true }} />
                                        </Grid>
                                    )}
                                </Grid>
                            ) : (
                                <Grid container spacing={1}>
                                    <Grid item xs={6}><TextField label="Name *" size="small" fullWidth value={form.assignedCustomName} onChange={e => handleField('assignedCustomName', e.target.value)} /></Grid>
                                    <Grid item xs={6}><TextField label="Phone *" size="small" fullWidth value={form.assignedCustomPhone} onChange={e => handleField('assignedCustomPhone', e.target.value)} /></Grid>
                                </Grid>
                            )}
                        </CardContent>
                    </Card>

                    {/* Equipment Type */}
                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Equipment Type</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {EQUIPMENT_TYPES.map(t => (
                                    <Button key={t.value} variant={form.equipmentType === t.value ? 'contained' : 'outlined'}
                                        onClick={() => handleField('equipmentType', t.value)}
                                        sx={{
                                            borderColor: t.color, fontWeight: 600, fontSize: '0.8rem',
                                            color: form.equipmentType === t.value ? '#fff' : t.color,
                                            backgroundColor: form.equipmentType === t.value ? t.color : 'transparent',
                                            '&:hover': { backgroundColor: form.equipmentType === t.value ? t.color : `${t.color}15`, borderColor: t.color },
                                        }}>
                                        {t.label}
                                    </Button>
                                ))}
                            </Box>
                            {formTypeConfig && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    Inspection form: <strong>{formTypeConfig.label}</strong>
                                </Typography>
                            )}
                        </CardContent>
                    </Card>

                    {/* Equipment Details — per type */}
                    {form.equipmentType && (
                        <Card sx={{ mb: 2 }}>
                            <CardContent>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Equipment Details</Typography>
                                <Grid container spacing={1}>
                                    {form.equipmentType === 'aerial_manlift' && (<>
                                        <Grid item xs={12}>
                                            <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Type</Typography>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                {['Scissor', 'Boom', 'Manlift'].map(t => (
                                                    <Button key={t} size="small" variant={form.unitSubType === t.toLowerCase() ? 'contained' : 'outlined'}
                                                        onClick={() => handleField('unitSubType', t.toLowerCase())}
                                                        sx={{ flex: 1, fontWeight: 600, fontSize: '0.8rem',
                                                            ...(form.unitSubType === t.toLowerCase() ? { backgroundColor: '#e65100', borderColor: '#e65100' } : { borderColor: '#e65100', color: '#e65100' }),
                                                        }}>{t}</Button>
                                                ))}
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}><TextField label="ID" size="small" fullWidth value={form.equipmentNumber} onChange={e => handleField('equipmentNumber', e.target.value)} /></Grid>
                                        <Grid item xs={6}><TextField label="MFG" size="small" fullWidth value={form.manufacturer} onChange={e => handleField('manufacturer', e.target.value)} /></Grid>
                                        <Grid item xs={6}><TextField label="Owner" size="small" fullWidth value={form.owner} onChange={e => handleField('owner', e.target.value)} /></Grid>
                                    </>)}
                                    {form.equipmentType === 'crane' && (<>
                                        <Grid item xs={6}><TextField label="Model" size="small" fullWidth value={form.model} onChange={e => handleField('model', e.target.value)} /></Grid>
                                        <Grid item xs={6}><TextField label="Serial #" size="small" fullWidth value={form.serialNumber} onChange={e => handleField('serialNumber', e.target.value)} /></Grid>
                                        <Grid item xs={6}><TextField label="MFR" size="small" fullWidth value={form.manufacturer} onChange={e => handleField('manufacturer', e.target.value)} /></Grid>
                                        <Grid item xs={6}><TextField label="Owner" size="small" fullWidth value={form.owner} onChange={e => handleField('owner', e.target.value)} /></Grid>
                                    </>)}
                                    {form.equipmentType === 'forklift' && (<>
                                        <Grid item xs={6}><TextField label="Model" size="small" fullWidth value={form.model} onChange={e => handleField('model', e.target.value)} /></Grid>
                                        <Grid item xs={6}><TextField label="Serial #" size="small" fullWidth value={form.serialNumber} onChange={e => handleField('serialNumber', e.target.value)} /></Grid>
                                        <Grid item xs={6}><TextField label="MFR" size="small" fullWidth value={form.manufacturer} onChange={e => handleField('manufacturer', e.target.value)} /></Grid>
                                        <Grid item xs={6}><TextField label="Owner" size="small" fullWidth value={form.owner} onChange={e => handleField('owner', e.target.value)} /></Grid>
                                    </>)}
                                </Grid>
                            </CardContent>
                        </Card>
                    )}

                    {/* Location */}
                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Location</Typography>
                            <Autocomplete
                                options={locationOptions}
                                getOptionLabel={o => o.label || ''}
                                value={locationOptions.find(o => o.label === form.location) || null}
                                onChange={(_, v) => handleField('location', v?.label || '')}
                                renderInput={params => <TextField {...params} label="Location *" size="small" placeholder="Search location..." />}
                            />
                        </CardContent>
                    </Card>

                    <Button variant="contained" fullWidth size="large"
                        disabled={!form.equipmentType || !form.location || saving}
                        onClick={handleLink}
                        startIcon={saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <LinkIcon />}
                        sx={{ py: 1.5, fontWeight: 700, fontSize: '1rem' }}>
                        Link & Start Initial Inspection
                    </Button>
                </Box>
            </Box>

            <SelectSingleEmployeeModal open={employeeModalOpen} onClose={() => setEmployeeModalOpen(false)} onSelect={handleEmployeeSelect} />
        </Box>
    );
}
